/** Warning banner component for displaying provider quality alerts. */
import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Collapse,
  Box,
  Typography,
  Chip,
  Link,
  IconButton,
} from "@mui/material";
import { Warning as WarningIcon, ExpandMore, ExpandLess } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export interface ProviderAlertData {
  name: string;
  asr: number; // 45.2
  acd: number; // 55.3
  threshold_asr: number; // 50
  threshold_acd: number; // 60
}

interface QualityAlertBannerProps {
  providers: ProviderAlertData[];
}

/** Quality alert banner for providers below ASR/ACD thresholds. */
export default function QualityAlertBanner({
  providers,
}: QualityAlertBannerProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  // Hide banner if no providers below threshold
  if (!providers || providers.length === 0) {
    return null;
  }

  const handleDismiss = () => {
    setOpen(false);
  };

  const toggleDetails = () => {
    setDetailsExpanded(!detailsExpanded);
  };

  return (
    <Collapse in={open}>
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        onClose={handleDismiss}
        sx={{
          mb: 3,
          "& .MuiAlert-message": {
            width: "100%",
          },
        }}
        action={
          <IconButton
            aria-label={detailsExpanded ? "Show less" : "Show more"}
            onClick={toggleDetails}
            size="small"
          >
            {detailsExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
      >
        <AlertTitle>Quality Alert: Some providers are below quality thresholds</AlertTitle>
        <Typography variant="body2">
          {providers.length} provider{providers.length > 1 ? "s are" : " is"} experiencing
          quality issues. Click below to view details.
        </Typography>

        <Collapse in={detailsExpanded}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {providers.map((provider, index) => {
              const asrBelowThreshold = provider.asr < provider.threshold_asr;
              const acdBelowThreshold = provider.acd < provider.threshold_acd;

              return (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {/* Provider Name */}
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 120 }}>
                    {provider.name}
                  </Typography>

                  {/* ASR */}
                  <Chip
                    label={`ASR: ${provider.asr.toFixed(1)}%`}
                    size="small"
                    color={asrBelowThreshold ? "error" : "success"}
                    sx={{ fontWeight: 500 }}
                  />

                  {/* ACD */}
                  <Chip
                    label={`ACD: ${provider.acd.toFixed(0)}s`}
                    size="small"
                    color={acdBelowThreshold ? "error" : "success"}
                    sx={{ fontWeight: 500 }}
                  />

                  {/* Thresholds */}
                  <Typography variant="caption" color="text.secondary">
                    (Thresholds: ASR ≥ {provider.threshold_asr}%, ACD ≥ {provider.threshold_acd}s)
                  </Typography>

                  {/* Link to provider detail (placeholder) */}
                  <Link
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      // TODO: Navigate to provider detail page once it exists
                      navigate("/traffic");
                    }}
                    sx={{ ml: "auto", fontSize: "0.875rem" }}
                  >
                    View Details
                  </Link>
                </Box>
              );
            })}
          </Box>
        </Collapse>
      </Alert>
    </Collapse>
  );
}
